import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Sparkles, Plus, Trash2, Tag, Building2, Users, Briefcase, Package, Lock, Globe, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { Layout } from '@/components/wiki/Layout';
import { TipTapEditor } from '@/components/wiki/TipTapEditor';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_OPTIONS = [
  { id: 'Businesses', label: 'Businesses', icon: Building2 },
  { id: 'People', label: 'People', icon: Users },
  { id: 'Investors', label: 'Investors', icon: Briefcase },
  { id: 'Products', label: 'Products', icon: Package },
];

export default function CreatePage() {
  const navigate = useNavigate();
  const { user, getAuthHeader, isSuperAdmin } = useAuth();
  const [saving, setSaving] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [summary, setSummary] = useState('');
  const [infobox, setInfobox] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showPremiumAlert, setShowPremiumAlert] = useState(false);

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleCreate = async () => {
    if (!user) {
      toast.error('Please log in to create pages');
      navigate('/login');
      return;
    }

    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    const strippedContent = content.replace(/<p><\/p>/g, '').replace(/<[^>]*>/g, '').trim();
    if (!strippedContent) {
      toast.error('Content is required');
      return;
    }

    setSaving(true);
    try {
      const pageData = {
        title: title.trim(),
        content,
        summary: summary.trim() || null,
        infobox: Object.keys(infobox).length > 0 ? infobox : null,
        categories: selectedCategories.length > 0 ? selectedCategories : null,
      };

      const response = await axios.post(`${API}/pages`, pageData, {
        headers: getAuthHeader()
      });
      
      toast.success('Page created successfully!');
      navigate(`/wiki/${response.data.slug}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create page');
    } finally {
      setSaving(false);
    }
  };

  const addInfoboxField = () => {
    if (newFieldName && newFieldName.trim()) {
      setInfobox({ ...infobox, [newFieldName.trim()]: '' });
      setNewFieldName('');
      setFieldDialogOpen(false);
      toast.success(`Added field: ${newFieldName.trim()}`);
    }
  };

  const removeInfoboxField = (key) => {
    const newInfobox = { ...infobox };
    delete newInfobox[key];
    setInfobox(newInfobox);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8" data-testid="create-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-[#36c] hover:text-[#2a4b8d] hover:bg-[#eaecf0]" data-testid="back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="font-serif text-2xl font-semibold text-[#202122]">
              Create New Page
            </h1>
          </div>
          <Button 
            onClick={handleCreate} 
            disabled={saving || !user}
            className="bg-[#36c] hover:bg-[#2a4b8d] text-white font-medium"
            data-testid="create-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Creating...' : 'Create Page'}
          </Button>
        </div>

        {!user && (
          <div className="bg-[#eaf3ff] border border-[#36c]/30 rounded-sm p-4 mb-6">
            <p className="text-[#202122]">
              Please <Link to="/login" className="text-[#36c] underline font-medium">log in</Link> or{' '}
              <Link to="/register" className="text-[#36c] underline font-medium">register</Link> to create a page.
            </p>
          </div>
        )}

        {/* Claim Page Banner */}
        <div className="bg-[#f8f9fa] border border-[#c8ccd1] rounded-sm p-6 mb-6" data-testid="claim-banner">
          <div className="flex flex-col">
            <h3 className="font-medium text-[#202122] flex items-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-[#36c]" />
              Claim this page
            </h3>
            <p className="text-sm text-[#54595d] mb-4">
              Add official contact info, social links, and lock editing. Hire a writer to create your page and get lifetime premium benefits.
            </p>
            <Link to="/pricing">
              <Button className="bg-[#36c] hover:bg-[#2a4b8d] text-white w-full sm:w-auto font-medium" data-testid="claim-cta-btn">
                From $69/mo
              </Button>
            </Link>
          </div>
          
          {/* Super Admin Notice */}
          {isSuperAdmin() && (
            <div className="mt-4 pt-4 border-t border-[#c8ccd1]">
              <p className="text-xs text-[#36c] flex items-center gap-2">
                <Sparkles className="h-3 w-3" />
                As a Super Admin, you can add premium features to any page without payment.
              </p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title */}
            <div className="bg-white rounded-sm border border-[#a2a9b1] p-6">
              <Label htmlFor="title" className="text-sm font-medium text-[#202122] mb-2 block">
                Page Title *
              </Label>
              <Input
                id="title"
                placeholder="e.g., Apple Inc., Elon Musk, Sequoia Capital"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="text-lg bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d]"
                data-testid="title-input"
              />
            </div>

            {/* Summary */}
            <div className="bg-white rounded-sm border border-[#a2a9b1] p-6">
              <Label htmlFor="summary" className="text-sm font-medium text-[#202122] mb-2 block">
                Article Summary
              </Label>
              <Textarea
                id="summary"
                placeholder="Brief description of this article..."
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                className="resize-none bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d]"
                rows={2}
                data-testid="summary-input"
              />
            </div>

            {/* Content Editor */}
            <div className="bg-white rounded-sm border border-[#a2a9b1] overflow-hidden">
              <div className="p-4 border-b border-[#c8ccd1] bg-[#f8f9fa]">
                <h2 className="font-medium text-[#202122]">Content *</h2>
                <p className="text-xs text-[#54595d] mt-1">Use the toolbar to format text, add links, images, and citations.</p>
              </div>
              <TipTapEditor 
                content={content} 
                onChange={setContent}
                placeholder="Start writing your article..."
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Infobox */}
            <div className="bg-white rounded-sm border border-[#a2a9b1] p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-medium text-[#202122]">Infobox</h2>
                <Dialog open={fieldDialogOpen} onOpenChange={setFieldDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="border-[#a2a9b1] text-[#54595d] hover:bg-[#eaecf0] hover:text-[#202122]" data-testid="add-infobox-field-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-[#a2a9b1]">
                    <DialogHeader>
                      <DialogTitle className="text-[#202122]">Add Infobox Field</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="field-name" className="text-[#202122]">Field Name</Label>
                        <Input
                          id="field-name"
                          placeholder="e.g., Founded, CEO, Industry"
                          value={newFieldName}
                          onChange={(e) => setNewFieldName(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && addInfoboxField()}
                          className="bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d]"
                          data-testid="new-field-name-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="outline" className="border-[#a2a9b1] text-[#54595d] hover:bg-[#eaecf0]">
                          Cancel
                        </Button>
                      </DialogClose>
                      <Button onClick={addInfoboxField} className="bg-[#36c] hover:bg-[#2a4b8d] text-white" data-testid="confirm-add-field-btn">
                        Add Field
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <p className="text-xs text-[#54595d] mb-3">Add structured data that appears in the sidebar.</p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-[#54595d]">Image URL</Label>
                  <Input
                    placeholder="https://..."
                    value={infobox.image || ''}
                    onChange={(e) => setInfobox({ ...infobox, image: e.target.value })}
                    className="bg-white border-[#a2a9b1] text-[#202122] placeholder:text-[#72777d]"
                    data-testid="infobox-image-input"
                  />
                </div>
                {Object.entries(infobox).filter(([key]) => key !== 'image').map(([key, value]) => (
                  <div key={key} className="flex gap-2">
                    <div className="flex-1">
                      <Label className="text-xs text-[#54595d]">{key}</Label>
                      <Input
                        value={value}
                        onChange={(e) => setInfobox({ ...infobox, [key]: e.target.value })}
                        className="bg-white border-[#a2a9b1] text-[#202122]"
                        data-testid={`infobox-${key}-input`}
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeInfoboxField(key)}
                      className="text-[#d33] hover:text-[#b32424] hover:bg-[#fee7e6] mt-5"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Categories */}
            <div className="bg-white rounded-sm border border-[#a2a9b1] p-6">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="h-4 w-4 text-[#36c]" />
                <h2 className="font-medium text-[#202122]">Categories</h2>
              </div>
              <p className="text-xs text-[#54595d] mb-3">Select one or more categories for this page.</p>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORY_OPTIONS.map((cat) => {
                  const isSelected = selectedCategories.includes(cat.id);
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => toggleCategory(cat.id)}
                      className={`flex items-center gap-2 p-3 rounded-sm border transition-all ${
                        isSelected 
                          ? 'bg-[#eaf3ff] border-[#36c] text-[#36c]' 
                          : 'bg-[#f8f9fa] border-[#c8ccd1] text-[#54595d] hover:border-[#a2a9b1] hover:text-[#202122]'
                      }`}
                      data-testid={`category-tile-${cat.id.toLowerCase()}`}
                    >
                      <cat.icon className="h-4 w-4" />
                      <span className="text-sm font-medium">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Premium Features Alert */}
            {showPremiumAlert && (
              <Alert className="bg-[#eaf3ff] border-[#36c]/30">
                <Lock className="h-4 w-4 text-[#36c]" />
                <AlertDescription className="text-[#202122]">
                  Social links, contact info, and website are only available after claiming this page.{' '}
                  <Link to="/pricing" className="text-[#36c] underline font-medium">Get credits to claim</Link>
                </AlertDescription>
              </Alert>
            )}

            {/* Social Links (Premium - Locked) */}
            <div 
              className="bg-white rounded-sm border border-[#c8ccd1] p-6 relative opacity-60 cursor-not-allowed"
              onClick={() => {
                setShowPremiumAlert(true);
                toast.info('Social links are only available after claiming this page');
              }}
              data-testid="social-links-section"
            >
              <div className="absolute inset-0 flex items-center justify-center bg-white/50 rounded-sm z-10">
                <div className="flex items-center gap-2 text-[#36c] bg-white px-3 py-1.5 rounded-full border border-[#36c]/30">
                  <Lock className="h-3 w-3" />
                  <span className="text-xs font-medium">Claim to unlock</span>
                </div>
              </div>
              <h2 className="font-medium text-[#202122] mb-4 flex items-center gap-2">
                <Globe className="h-4 w-4 text-[#36c]" />
                Social Links
              </h2>
              <div className="space-y-3 pointer-events-none">
                <div>
                  <Label className="text-xs text-[#54595d]">Website</Label>
                  <Input
                    placeholder="https://company.com"
                    disabled
                    className="bg-[#f8f9fa] border-[#c8ccd1] text-[#202122] placeholder:text-[#72777d]"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#54595d]">LinkedIn</Label>
                  <Input
                    placeholder="https://linkedin.com/company/..."
                    disabled
                    className="bg-[#f8f9fa] border-[#c8ccd1] text-[#202122] placeholder:text-[#72777d]"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#54595d]">Twitter/X</Label>
                  <Input
                    placeholder="https://twitter.com/..."
                    disabled
                    className="bg-[#f8f9fa] border-[#c8ccd1] text-[#202122] placeholder:text-[#72777d]"
                  />
                </div>
              </div>
            </div>

            {/* Contact Info (Premium - Locked) */}
            <div 
              className="bg-white rounded-sm border border-[#c8ccd1] p-6 relative opacity-60 cursor-not-allowed"
              onClick={() => {
                setShowPremiumAlert(true);
                toast.info('Contact info is only available after claiming this page');
              }}
              data-testid="contact-info-section"
            >
              <div className="absolute inset-0 flex items-center justify-center bg-white/50 rounded-sm z-10">
                <div className="flex items-center gap-2 text-[#36c] bg-white px-3 py-1.5 rounded-full border border-[#36c]/30">
                  <Lock className="h-3 w-3" />
                  <span className="text-xs font-medium">Claim to unlock</span>
                </div>
              </div>
              <h2 className="font-medium text-[#202122] mb-4 flex items-center gap-2">
                <Mail className="h-4 w-4 text-[#36c]" />
                Contact Information
              </h2>
              <div className="space-y-3 pointer-events-none">
                <div>
                  <Label className="text-xs text-[#54595d]">Email</Label>
                  <Input
                    type="email"
                    placeholder="contact@company.com"
                    disabled
                    className="bg-[#f8f9fa] border-[#c8ccd1] text-[#202122] placeholder:text-[#72777d]"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#54595d]">Phone</Label>
                  <Input
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    disabled
                    className="bg-[#f8f9fa] border-[#c8ccd1] text-[#202122] placeholder:text-[#72777d]"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#54595d]">Address</Label>
                  <Textarea
                    placeholder="123 Main St, City, State 12345"
                    disabled
                    rows={2}
                    className="bg-[#f8f9fa] border-[#c8ccd1] text-[#202122] placeholder:text-[#72777d]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
